/**
 * WorkUnitCalculator — the one "what does a month buy" section.
 *
 * 2026-08-19 owner directive: /pricing and /subscription were showing the same
 * arithmetic three ways — the full rate table, the worked-month sentence, and
 * the single-product ceilings sentence — which is a lot of numbers to hand a
 * buyer who only wants to know whether 2,200 is enough for their firm. This
 * replaces all three with ONE section: five sliders, preloaded to a real
 * month, and a meter.
 *
 * Every number comes from src/config/workUnits.ts:
 *   - the starting position of each slider is TYPICAL_MONTH.count
 *   - each rate is TYPICAL_MONTH.wuEach, pinned to WORK_RATE_CARD by CI
 *     (scripts/check-public-pricing-sync.mjs)
 *   - each slider's MAXIMUM is singleProductCeiling() — 2,200 ÷ that rate — so
 *     dragging one row to the end shows what a month of only that product
 *     holds. That is what the old ceilings sentence said, made discoverable.
 * Nothing here hard-codes a rate, so a re-measure propagates on its own.
 *
 * Styling follows the pricing page's system: brand gold #C6A75E on warm
 * neutrals, Space Grotesk headings over Inter, lowercase rendering with
 * natural-case source (the guest knowledge base is generated from DOM text, so
 * product names must stay capitalized in source — see Pricing.tsx FeaturesSub).
 */
import React from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import ShinyGoldButton from './ShinyGoldButton';
import KeepCase from '../common/KeepCase';
import { APP_OVERLAY_Z_INDEX } from '../../styles/layers';
import {
  MONTHLY_WU_PER_PRESTON,
  TYPICAL_MONTH,
  WU_LABEL,
  singleProductCeiling,
  typicalMonthLineWu,
} from '../../config/workUnits';

const GOLD = 'var(--brand-gold)';
const GOLD_DEEP = 'var(--brand-gold-hover)';
const GOLD_INK = '#8A6F2F';
const TRACK = '#EFE9DC';

/* On /pricing this sits on the warm page ground, where the platform's content
   cards are white on a neutral hairline. On /subscription it nests inside
   RecCard's gold gradient (#FFFDF7 -> #F5ECD3, gold border), where a neutral
   grey hairline reads cold — so the compact variant borrows the card's own
   gold at low alpha and sits slightly translucent over it. */
const Shell = styled.div<{ $compact?: boolean }>`
  background: ${(p) => (p.$compact ? 'rgba(255, 255, 255, 0.72)' : '#fff')};
  border: 1px solid ${(p) => (p.$compact ? 'rgba(200, 167, 94, 0.35)' : '#EBEBEA')};
  border-radius: 1rem;
  padding: ${(p) => (p.$compact ? '1rem 1.05rem 1.1rem' : '1.5rem 1.6rem 1.6rem')};
  box-sizing: border-box;
  width: 100%;
`;

const Rows = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.95rem;
`;

const Row = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const RowHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
`;

const RowLabel = styled.label`
  font-size: 0.84rem;
  color: var(--brand-text-secondary);
  text-transform: lowercase;
  cursor: pointer;
`;

const RowValue = styled.span`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 0.9rem;
  font-weight: 650;
  color: var(--brand-text);
  font-variant-numeric: tabular-nums;
  white-space: nowrap;

  small {
    font-family: 'Inter', sans-serif;
    font-weight: 400;
    font-size: 0.72rem;
    color: var(--brand-text-muted);
    margin-left: 0.3rem;
  }
`;

/* Range input painted with a gold fill up to the thumb. The gradient stop is
   set inline from the live value — a CSS-only fill is not possible
   cross-browser.
   Two things this has to defeat, both from src/styles/GlobalStyles.ts:114
   (`input, select, textarea { min-height: 44px; padding: 0.5rem }`, a mobile
   touch-target rule): min-height overrides `height`, and the padding would let
   the fill paint the full box. So the element stays 1.25rem tall — still a
   usable touch target — while the TRACK is painted as a 0.35rem band centred
   inside it via background-size, rather than being the element's own
   background. Verified in a production build; the first pass shipped a 44px
   gold block. */
const Slider = styled.input`
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 1.25rem;
  min-height: 0;
  padding: 0;
  border: none;
  background-color: transparent;
  background-repeat: no-repeat;
  background-position: center;
  background-size: 100% 0.35rem;
  outline: none;
  margin: 0.15rem 0 0;
  cursor: pointer;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 1rem;
    height: 1rem;
    border-radius: 50%;
    background: ${GOLD};
    border: 2px solid #fff;
    box-shadow: 0 1px 3px rgba(61, 48, 18, 0.28);
    cursor: pointer;
  }

  &::-moz-range-thumb {
    width: 1rem;
    height: 1rem;
    border: 2px solid #fff;
    border-radius: 50%;
    background: ${GOLD};
    box-shadow: 0 1px 3px rgba(61, 48, 18, 0.28);
    cursor: pointer;
  }

  &:focus-visible {
    box-shadow: 0 0 0 3px rgba(198, 167, 94, 0.35);
  }
`;

const Total = styled.div`
  margin-top: 1.25rem;
  padding-top: 1.05rem;
  border-top: 1px solid #EBEBEA;
`;

const TotalHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 0.75rem;
  margin-bottom: 0.5rem;
`;

const TotalNum = styled.span<{ $over: boolean }>`
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: -0.015em;
  color: ${(p) => (p.$over ? GOLD_INK : 'var(--brand-text)')};
  font-variant-numeric: tabular-nums;

  small {
    font-family: 'Inter', sans-serif;
    font-size: 0.78rem;
    font-weight: 400;
    color: var(--brand-text-muted);
    margin-left: 0.35rem;
  }
`;

const TotalNote = styled.span`
  font-size: 0.78rem;
  color: #6F6A60;
  text-transform: lowercase;
  text-align: right;
`;

const Meter = styled.div`
  height: 0.5rem;
  border-radius: 0.5rem;
  background: ${TRACK};
  overflow: hidden;
`;

const MeterFill = styled.div<{ $pct: number; $over: boolean }>`
  height: 100%;
  width: ${(p) => p.$pct}%;
  border-radius: 0.5rem;
  background: ${(p) => (p.$over ? GOLD_DEEP : GOLD)};
  transition: width 0.16s ease;
`;

const Foot = styled.p`
  margin: 0.85rem 0 0;
  font-size: 0.8rem;
  line-height: 1.6;
  color: #6F6A60;
  text-transform: lowercase;

  strong {
    color: var(--brand-text);
    font-weight: 650;
  }
`;

const Reset = styled.button`
  background: none;
  border: none;
  padding: 0;
  font: inherit;
  font-size: 0.78rem;
  color: ${GOLD_INK};
  text-transform: lowercase;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 0.15em;

  &:hover { color: ${GOLD_DEEP}; }
`;

type Counts = Record<string, number>;

const PRESET: Counts = TYPICAL_MONTH.reduce<Counts>(
  (acc, r) => ({ ...acc, [r.key]: r.count }),
  {}
);

export const WorkUnitCalculator: React.FC<{ compact?: boolean }> = ({ compact }) => {
  const [counts, setCounts] = React.useState<Counts>(PRESET);

  const total = TYPICAL_MONTH.reduce(
    (sum, r) => sum + typicalMonthLineWu({ count: counts[r.key] ?? 0, wuEach: r.wuEach }),
    0
  );
  const over = total > MONTHLY_WU_PER_PRESTON;
  const pct = Math.min(100, (total / MONTHLY_WU_PER_PRESTON) * 100);
  const remaining = MONTHLY_WU_PER_PRESTON - total;
  const isPreset = TYPICAL_MONTH.every((r) => (counts[r.key] ?? 0) === r.count);

  return (
    <Shell $compact={compact}>
      <Rows>
        {TYPICAL_MONTH.map((r) => {
          const value = counts[r.key] ?? 0;
          const max = singleProductCeiling(r.wuEach);
          const fill = (value / max) * 100;
          return (
            <Row key={r.key}>
              <RowHead>
                <RowLabel htmlFor={`wu-calc-${r.key}`}>{r.item}</RowLabel>
                <RowValue>
                  {value.toLocaleString('en-US')}
                  <small>
                    {typicalMonthLineWu({ count: value, wuEach: r.wuEach }).toLocaleString('en-US')} {WU_LABEL}
                  </small>
                </RowValue>
              </RowHead>
              <Slider
                id={`wu-calc-${r.key}`}
                type="range"
                min={0}
                max={max}
                step={1}
                value={value}
                aria-valuetext={`${value} ${r.item}`}
                style={{
                  // backgroundImage only — size/position/repeat live in the
                  // styled rule so the painted band stays 0.35rem tall inside
                  // the taller, tappable input box.
                  backgroundImage: `linear-gradient(to right, ${GOLD} 0%, ${GOLD} ${fill}%, ${TRACK} ${fill}%, ${TRACK} 100%)`,
                }}
                onChange={(e) =>
                  setCounts((c) => ({ ...c, [r.key]: Number(e.target.value) }))
                }
              />
            </Row>
          );
        })}
      </Rows>

      <Total>
        <TotalHead>
          <TotalNum $over={over} aria-live="polite">
            {Math.round(total).toLocaleString('en-US')}
            <small>of {MONTHLY_WU_PER_PRESTON.toLocaleString('en-US')} {WU_LABEL}</small>
          </TotalNum>
          <TotalNote>
            {over
              ? `${Math.abs(remaining).toLocaleString('en-US')} ${WU_LABEL} into overtime`
              : `${remaining.toLocaleString('en-US')} ${WU_LABEL} still free`}
          </TotalNote>
        </TotalHead>
        <Meter
          role="progressbar"
          aria-valuemin={0}
          aria-valuemax={MONTHLY_WU_PER_PRESTON}
          aria-valuenow={Math.round(Math.min(total, MONTHLY_WU_PER_PRESTON))}
        >
          <MeterFill $pct={pct} $over={over} />
        </Meter>
        <Foot>
          {over ? (
            <>
              past your month, work keeps going — overtime bills at the standard rate,
              and nothing beyond your committed team charges without your administrator's
              approval.
            </>
          ) : (
            <>
              one <KeepCase>Prest0n</KeepCase> professional, one month. drag any row to see what a month of
              only that work holds.
            </>
          )}{' '}
          {!isPreset && <Reset type="button" onClick={() => setCounts(PRESET)}>reset to a typical month</Reset>}
        </Foot>
      </Total>
    </Shell>
  );
};

// ── Launcher: a quiet trigger + a subtle modal ─────────────────────────────
// 2026-08-19 owner follow-up ("maybe a subtle pop up to open the calculator —
// it does not have to be too much text nor overwhelm"). Inline, the calculator
// still put five sliders and a meter in front of everyone. Behind a one-line
// trigger, the page reads as prose and the numbers are there for whoever wants
// them.
//
// The modal PORTALS to document.body deliberately: on /subscription this
// mounts inside RecCard, which sets `overflow: hidden` — an in-tree modal
// would be clipped by it.

const fadeIn = keyframes`from { opacity: 0 } to { opacity: 1 }`;
const riseIn = keyframes`
  from { opacity: 0; transform: translateY(0.5rem) }
  to   { opacity: 1; transform: none }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  /* A portaled modal backdrop: it must clear the chat in every mode, so it
     rides the app rung rather than a hand-picked page number. */
  z-index: ${APP_OVERLAY_Z_INDEX};
  background: rgba(43, 34, 12, 0.34);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1.25rem;
  animation: ${fadeIn} 0.16s ease both;

  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const Panel = styled.div`
  width: 100%;
  max-width: 32rem;
  max-height: calc(100vh - 2.5rem);
  overflow-y: auto;
  background: #FCFBF7;
  border: 1px solid rgba(200, 167, 94, 0.45);
  border-radius: 1rem;
  box-shadow: 0 1.5rem 3rem rgba(61, 48, 18, 0.22);
  padding: 1.15rem 1.15rem 1.25rem;
  animation: ${riseIn} 0.2s ease both;

  @media (prefers-reduced-motion: reduce) { animation: none; }
`;

const PanelHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 0.85rem;
`;

const PanelTitle = styled.h3`
  margin: 0;
  font-family: 'Space Grotesk', 'Inter', sans-serif;
  font-size: 0.98rem;
  font-weight: 650;
  letter-spacing: -0.015em;
  text-transform: lowercase;
  color: var(--brand-text);
`;

const CloseBtn = styled.button`
  background: none;
  border: none;
  padding: 0.15rem 0.35rem;
  font-size: 1.15rem;
  line-height: 1;
  color: var(--brand-text-muted);
  cursor: pointer;
  border-radius: 0.35rem;

  &:hover { color: #3D3012; }
  &:focus-visible { outline: 2px solid ${GOLD}; outline-offset: 2px; }
`;

const Trigger = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: none;
  padding: 0;
  font-family: 'Inter', sans-serif;
  font-size: 0.85rem;
  color: ${GOLD_INK};
  text-transform: lowercase;
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 0.2em;
  text-decoration-color: rgba(198, 167, 94, 0.5);

  &:hover { color: ${GOLD_DEEP}; text-decoration-color: ${GOLD_DEEP}; }
  &:focus-visible { outline: 2px solid ${GOLD}; outline-offset: 3px; border-radius: 0.2rem; }

  span[aria-hidden] { transition: transform 0.14s ease; }
  &:hover span[aria-hidden] { transform: translateX(2px); }
`;

/* variant="bold": the shared shiny pill at its large size. */
const BoldLauncherTrigger = styled(ShinyGoldButton).attrs({ $size: 'lg' as const })``;

/* In-modal conversion row (cta="signup"): the calculator is the moment of
 * highest intent — the buyer has just seen what a month holds — so the ONE
 * primary action repeats here instead of adding a third competing button on
 * the page (decision-fatigue strategy, 2026-08-19 owner directive). */
const ModalCtaRow = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.45rem;
  margin-top: 1.1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(200, 167, 94, 0.28);
`;


const ModalCtaNote = styled.p`
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-size: 0.78rem;
  color: #6F6A60;
  text-transform: lowercase;
  text-align: center;
`;

export const WorkCalculatorLauncher: React.FC<{
  label?: string;
  /** 'bold' = the large shiny gold pill; default keeps the quiet text link. */
  variant?: 'quiet' | 'bold';
  /** 'signup' adds the create-account CTA inside the modal (public pages only). */
  cta?: 'signup' | 'none';
}> = ({
  label = 'see what a month buys',
  variant = 'quiet',
  cta = 'none',
}) => {
  const [open, setOpen] = React.useState(false);
  const restoreTo = React.useRef<HTMLElement | null>(null);
  const panelRef = React.useRef<HTMLDivElement | null>(null);

  React.useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    panelRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      restoreTo.current?.focus();
    };
  }, [open]);

  const navigate = useNavigate();
  const TriggerComponent = variant === 'bold' ? BoldLauncherTrigger : Trigger;

  return (
    <>
      <TriggerComponent
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={(e) => { restoreTo.current = e.currentTarget; setOpen(true); }}
      >
        {label} <span aria-hidden="true">→</span>
      </TriggerComponent>

      {open && typeof document !== 'undefined' && createPortal(
        <Backdrop
          data-prest0n-portal="true"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(false); }}
        >
          <Panel
            ref={panelRef}
            role="dialog"
            aria-modal="true"
            aria-label="what a month of work buys"
            tabIndex={-1}
          >
            <PanelHead>
              <PanelTitle>what a month buys</PanelTitle>
              <CloseBtn type="button" aria-label="close" onClick={() => setOpen(false)}>×</CloseBtn>
            </PanelHead>
            <WorkUnitCalculator compact />
            {cta === 'signup' && (
              <ModalCtaRow>
                <ShinyGoldButton
                  type="button"
                  onClick={() => { setOpen(false); navigate('/auth', { state: { authMode: 'signup', returnUrl: '/' } }); }}
                >
                  create your account — over 100 jobs on us
                </ShinyGoldButton>
                <ModalCtaNote>14-day evaluation · no credit card · about two minutes to set up</ModalCtaNote>
              </ModalCtaRow>
            )}
          </Panel>
        </Backdrop>,
        document.body
      )}
    </>
  );
};

export default WorkUnitCalculator;
